import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useCallback, useState } from "react";

import type { Frame } from "@/types/dragon-tiles";

import { createFrame, getFrame } from "@/api/dragon-tiles";
import { buildCreateFramePayloadFromSourceFrame } from "@/features/BroadcastV2/duplicateBroadcastV2";
import { variationLabel } from "@/features/BroadcastV2/store/variationTypes";
import { enqueueErrorSnackbar } from "@/utils/snackbarHelpers";

type DuplicateGuardChoice = "duplicate" | "choose-different";

interface UseVariationDuplicateGuardOptions {
  siteId: string;
  abEnabled: boolean;
  activeVariationIndex: number;
  /** Frame ids currently used by every OTHER variation. */
  getUsedFrameIdsByOtherVariations: () => string[];
  onFrameAccepted: (variationIndex: number, frame: Frame) => void;
}

/**
 * Guards against two variations sharing the same tile: when the picked frame is
 * already used by another variation, prompts the user to duplicate the tile
 * (so each variation owns a distinct frame) or pick a different one. Generalizes
 * to any number of variations.
 */
export function useVariationDuplicateGuard({
  siteId,
  abEnabled,
  activeVariationIndex,
  getUsedFrameIdsByOtherVariations,
  onFrameAccepted,
}: UseVariationDuplicateGuardOptions) {
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingFrame, setPendingFrame] = useState<Frame | null>(null);
  const [tileName, setTileName] = useState("");
  const [tileNameError, setTileNameError] = useState("");

  const createFrameMutation = useMutation({
    mutationFn: async (args: { frame: Frame; name: string }) => {
      // The frame picker hands back a slim frame whose variants carry no
      // content_text/content_json, so building the duplicate straight from it
      // yields an empty tile (FOG-1986). Fetch the full frame first — same
      // hydration duplicateBroadcastV2 and onFrameAccepted already do.
      const fullFrame = await getFrame(siteId, args.frame.id);
      const payload = buildCreateFramePayloadFromSourceFrame(
        fullFrame,
        siteId,
        args.name,
      );
      return createFrame(siteId, payload);
    },
  });

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setPendingFrame(null);
    setTileName("");
    setTileNameError("");
  }, []);

  const handleFrameSelect = useCallback(
    (frame: Frame) => {
      if (!abEnabled) {
        onFrameAccepted(activeVariationIndex, frame);
        return;
      }

      const usedByOthers = getUsedFrameIdsByOtherVariations();
      if (!usedByOthers.includes(frame.id)) {
        onFrameAccepted(activeVariationIndex, frame);
        return;
      }

      setPendingFrame(frame);
      setTileName(`${frame.name} (${variationLabel(activeVariationIndex)})`);
      setTileNameError("");
      setModalOpen(true);
    },
    [
      abEnabled,
      activeVariationIndex,
      getUsedFrameIdsByOtherVariations,
      onFrameAccepted,
    ],
  );

  const handleSelect = useCallback(
    (choiceId: DuplicateGuardChoice) => {
      if (choiceId === "choose-different") {
        closeModal();
        return;
      }

      if (!pendingFrame) return;

      const trimmed = tileName.trim();
      if (!trimmed) {
        setTileNameError("Tile name is required");
        return;
      }

      createFrameMutation.mutate(
        { frame: pendingFrame, name: trimmed },
        {
          onSuccess: (newFrame) => {
            onFrameAccepted(activeVariationIndex, newFrame);
            enqueueSnackbar("Tile duplicated", { variant: "success" });
            closeModal();
          },
          onError: (error) => {
            enqueueErrorSnackbar(error, "Failed to duplicate tile");
          },
        },
      );
    },
    [
      pendingFrame,
      tileName,
      createFrameMutation,
      activeVariationIndex,
      onFrameAccepted,
      closeModal,
    ],
  );

  return {
    handleFrameSelect,
    isDuplicating: createFrameMutation.isPending,
    duplicateGuardModal: {
      open: modalOpen,
      onClose: closeModal,
      tileName,
      onTileNameChange: (value: string) => {
        setTileName(value);
        if (tileNameError) setTileNameError("");
      },
      tileNameError,
      isPending: createFrameMutation.isPending,
      onSelect: handleSelect,
    },
  };
}
